import { TooltipButton } from '@/ui/TooltipButton';
import styled from 'styled-components';
import { softControlMotion } from '@/theme/interactions';
import type { ButtonHTMLAttributes } from 'react';
import { Icon, type IconName } from '@/icons';

export interface IconButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'aria-label'
> {
  icon: IconName;
  label: string;
  size?: number;
}

const Styled = styled(TooltipButton)`
  display: inline-grid;
  place-items: center;
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid transparent;
  background: transparent;
  color: ${({ theme }) => theme.color.textMuted};
  cursor: pointer;
  transition:
    background 140ms ease,
    color 140ms ease;
  &:hover {
    background: ${({ theme }) => theme.color.hover};
    color: ${({ theme }) => theme.color.textPrimary};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  /* DisabledHint contract: disabled-looking but still focusable, never the
     native disabled attribute (see ui/DisabledHint). */
  &[aria-disabled='true'] {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &[aria-disabled='true']:hover {
    background: transparent;
    color: ${({ theme }) => theme.color.textMuted};
  }
  ${softControlMotion}
`;

export function IconButton({ icon, label, size = 20, type = 'button', ...rest }: IconButtonProps) {
  return (
    <Styled type={type} aria-label={label} {...rest}>
      <Icon name={icon} size={size} />
    </Styled>
  );
}
